import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import format from 'date-fns/format';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils } from '@eluve/apollo-client';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Icon,
  Input,
  SimpleTooltip,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import {
  basicPatientDetailsFragment,
  useAppointmentDoctorName,
  useAppointmentId,
  useAppointmentPatient,
  useAppointmentStartDate,
  useTenantId,
} from '@eluve/frontend-appointment-hooks';
import { OutgoingEmailTemplateTypesLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { useTenantName } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';
import { getUserName } from '@eluve/utils';

const updatePatientEmailMutation = graphql(`
  mutation UpdatePatientEmail($tenantId: uuid!, $id: uuid!, $email: citext!) {
    updatePatientsByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { email: $email }
    ) {
      __typename
      id
      email
    }
  }
`);

const formSchema = z.object({
  email: z.string().email(),
  subject: z.string().min(1, 'Subject cannot be empty'),
  message: z.string().min(1, 'Message cannot be empty'),
  updatePatientEmail: z.boolean().default(false),
});

type Form = z.infer<typeof formSchema>;

const getEmailSubject = (appointmentStartDate: string, tenantName: string) =>
  `Your Superbill for ${appointmentStartDate} - ${tenantName}`;
const getEmailBody = (
  patientFirstName: string,
  appointmentStartDate: string,
  doctorName?: string,
) =>
  `Hello${patientFirstName ? ` ${patientFirstName}` : ''}, attached is your Superbill for your ` +
  `visit${doctorName ? ` with Dr. ${doctorName}` : ''} on ${appointmentStartDate}.`;

export const AppointmentEmailSuperbillDialog: React.FC = () => {
  const logger = useNamedLogger('SuperbillEmailForm');
  const api = useApiClient();
  const toast = useToast();

  const tenantId = useTenantId();
  const userId = useUserIdFromSession();
  const tenantName = useTenantName(tenantId);
  const appointmentId = useAppointmentId();
  const doctorName = useAppointmentDoctorName();
  const appointmentStartDate = useAppointmentStartDate();
  const patient = useAppointmentPatient();

  const analytics = useAnalytics('artifacts');
  const analyticsEventProperties = {
    tenantId,
    userId,
    appointmentId,
  };

  const { isDialogOpen, toggleDialog } = useDialog();
  const [success, setSuccess] = useState(false);

  const appointmentStartDateFormatted = format(
    new Date(appointmentStartDate),
    'PP',
  );

  const userName = getUserName({
    firstName: patient?.firstName,
    lastName: patient?.lastName,
  });

  const [updatePatientEmailAction] = useMutation(updatePatientEmailMutation, {
    optimisticResponse: ({ email, id }) => {
      return {
        updatePatientsByPk: {
          __typename: 'Patients' as const,
          id: id,
          email: email,
        },
      };
    },
    onError: () => {
      toast.error("The patient's email could not be updated.");
    },
    update: (_, { data }) => {
      const patientId = data?.updatePatientsByPk?.id;
      const email = data?.updatePatientsByPk?.email;
      if (!patientId || !email) {
        return;
      }
      cacheUtils.updateFragment(
        {
          fragment: basicPatientDetailsFragment,
          key: { id: patientId },
        },
        (existing) => {
          if (!existing) {
            return;
          }
          return {
            ...existing,
            email,
          };
        },
      );
    },
  });

  const onSubmit = async ({
    email,
    subject,
    message,
    updatePatientEmail,
  }: Form) => {
    analytics.capture('email_superbill_submit', analyticsEventProperties);

    if (updatePatientEmail && patient?.id) {
      await updatePatientEmailAction({
        variables: {
          id: patient.id,
          tenantId: tenantId,
          email,
        },
      });
    }

    const response = await api.appointments.sendEmail({
      params: {
        tenantId,
        appointmentId,
      },
      body: {
        email: email,
        subject: subject,
        message: message,
        type: OutgoingEmailTemplateTypesLookup.SUPERBILL,
      },
    });

    if (response.status === 200 && response.body.emailId) {
      analytics.capture('email_superbill_success', analyticsEventProperties);
      setSuccess(true);
    } else {
      analytics.capture('email_superbill_fail', analyticsEventProperties);
      toast.error('The email could not be sent. Please try again later!');
    }
  };

  const form = useForm<Form>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      email: patient?.email ?? patient?.external_patients_info[0]?.email ?? '',
      subject: getEmailSubject(appointmentStartDateFormatted, tenantName),
      message: getEmailBody(
        userName,
        appointmentStartDateFormatted,
        doctorName ?? undefined,
      ),
      updatePatientEmail: false,
    },
  });

  const handleDialogClose = () => {
    setSuccess(false);
    // We have to do this because we either reset to a default state
    // which contains an email update prior to a potential update,
    // or set the email address to newly updated value.
    // Though the cache is updated when there is a response,
    // the form has it's own default state we set above in useForm hook,
    // which contains the pre-updated email value.
    form.reset({
      ...form.formState.defaultValues,
      email: form.getValues('updatePatientEmail')
        ? form.getValues('email')
        : form.formState.defaultValues?.email,
    });
    toggleDialog();
  };

  if (success) {
    return (
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="flex flex-col gap-y-10">
          <DialogHeader>
            <Stack gap={3}>
              <DialogTitle className="w-full text-center text-2xl font-semibold text-text-primary">
                Email Sent
              </DialogTitle>
              <DialogDescription className="w-full text-center text-sm font-medium text-text-secondary">
                Your email has been successfully sent out.
              </DialogDescription>
            </Stack>
          </DialogHeader>
          <Button onClick={handleDialogClose}>Got it</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
      <Form {...form}>
        <SimpleTooltip label="Email PDF">
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Icon name="Mail" size="auto" />
            </Button>
          </DialogTrigger>
        </SimpleTooltip>
        <DialogContent size="lg">
          <Stack direction="horizontal" gap={3} align="stretch">
            <DialogHeader>
              <DialogTitle>Email Patient</DialogTitle>
              <div>
                <DialogDescription className="font-medium">
                  Please review the information before sending
                </DialogDescription>
              </div>
            </DialogHeader>
          </Stack>
          <form
            onSubmit={form.handleSubmit(onSubmit, (invalid) =>
              logger.debug(
                `Invalid form submission attempted: ${JSON.stringify(invalid)}`,
              ),
            )}
            className="flex flex-col gap-6"
          >
            <Stack gap={2}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-3xs font-bold text-text-supporting uppercase">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Email Address" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="updatePatientEmail"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-medium text-text-secondary">
                      Also update the patient's email on their account
                    </FormLabel>
                  </FormItem>
                )}
              />
            </Stack>
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-3xs font-bold text-text-supporting uppercase">
                    Subject
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Your superbill" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem className="max-w-lg">
                  <FormLabel className="text-3xs font-bold text-text-supporting uppercase">
                    Message
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="bg-white/5"
                      placeholder="Description"
                      rows={5}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                disabled={
                  !form.formState.isValid || form.formState.isSubmitting
                }
                type="submit"
              >
                Send Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  );
};
